import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

// Session configuration
const SESSION_CHECK_INTERVAL = 60000; // Check session every 1 minute
const ACTIVITY_DEBOUNCE = 5000; // Debounce activity refresh calls (5 seconds)
const WARNING_THRESHOLD = 5; // Show warning when 5 minutes remaining

// Activity events to track
const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

/**
 * Hook to manage session expiration and activity tracking.
 * Refreshes session on user activity and handles expiration.
 */
export const useSessionManager = (isAuthenticated, logout) => {
  const navigate = useNavigate();
  const lastActivityRef = useRef(Date.now());
  const activityTimeoutRef = useRef(null);
  const sessionCheckIntervalRef = useRef(null);
  const warningShownRef = useRef(false);

  // Refresh session on the backend
  const refreshSession = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return false;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/session/refresh`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        const remaining = response.data.remaining_minutes;
        
        // Show warning if session is about to expire
        if (remaining <= WARNING_THRESHOLD && remaining > 0 && !warningShownRef.current) {
          warningShownRef.current = true;
          toast.warning(`Session expires in ${remaining} minutes. Activity will extend it.`, {
            duration: 10000,
          });
        } else if (remaining > WARNING_THRESHOLD) {
          warningShownRef.current = false;
        } else if (remaining <= 0) {
          // Session already expired, don't show warning — just refresh silently
          warningShownRef.current = false;
        }
        
        return true;
      }
      return false;
    } catch (error) {
      // Check if session expired
      if (error.response?.status === 401 || 
          error.response?.headers?.['x-session-expired'] === 'true') {
        handleSessionExpired();
        return false;
      }
      console.error('Session refresh error:', error);
      return false;
    }
  }, []);

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    console.log('[SessionManager] Session expired');
    
    // Clear local storage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    
    // Call logout if provided
    if (logout) {
      logout();
    }
    
    // Navigate to login silently
    navigate('/login');
  }, [logout, navigate]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    if (!isAuthenticated) return;
    
    lastActivityRef.current = Date.now();
    
    // Debounce the session refresh
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    activityTimeoutRef.current = setTimeout(() => {
      refreshSession();
    }, ACTIVITY_DEBOUNCE);
  }, [isAuthenticated, refreshSession]);

  // Check session status periodically
  const checkSession = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const token = localStorage.getItem('access_token');
    if (!token) {
      handleSessionExpired();
      return;
    }

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/session/info`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const { remaining_minutes, is_active } = response.data;
      
      if (!is_active || remaining_minutes <= 0) {
        handleSessionExpired();
        return;
      }
      
      // Show warning if approaching expiration
      if (remaining_minutes <= WARNING_THRESHOLD && remaining_minutes > 0 && !warningShownRef.current) {
        warningShownRef.current = true;
        toast.warning(`Session expires in ${remaining_minutes} minutes. Continue working to stay logged in.`, {
          duration: 10000,
        });
      } else if (remaining_minutes > WARNING_THRESHOLD) {
        warningShownRef.current = false;
      }
    } catch (error) {
      if (error.response?.status === 401 || 
          error.response?.headers?.['x-session-expired'] === 'true') {
        handleSessionExpired();
      }
    }
  }, [isAuthenticated, handleSessionExpired]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    // Add activity listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Set up periodic session check
    sessionCheckIntervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);

    // Delay initial session check to avoid racing with login flow
    const initialCheckTimeout = setTimeout(checkSession, 2000);

    return () => {
      // Cleanup activity listeners
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      
      // Clear intervals and timeouts
      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
      clearTimeout(initialCheckTimeout);
    };
  }, [isAuthenticated, handleActivity, checkSession]);

  return {
    refreshSession,
    handleSessionExpired,
  };
};

/**
 * Setup axios interceptor to handle 401 responses globally.
 * Should be called once at app initialization.
 */
export const setupSessionInterceptor = (logoutCallback) => {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // Check for session expired
      if (error.response?.status === 401) {
        const sessionExpired = error.response?.headers?.['x-session-expired'] === 'true' ||
                              error.response?.data?.detail === 'Session expired';
        
        if (sessionExpired) {
          console.log('[SessionInterceptor] Session expired detected');
          
          // Clear storage
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          localStorage.setItem('session_expired', 'true');
          
          // Call logout
          if (logoutCallback) {
            logoutCallback();
          }
          
          // Redirect to login (if not already there)
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login?expired=true';
          }
        }
      }
      
      return Promise.reject(error);
    }
  );
};

export default useSessionManager;
